import React, { useState, useCallback } from 'react';
import Modal from './Modal';

const DEFAULT_TYPE_OPTIONS = [
  'Email', 'SMS', 'Letter', 'WhatsApp', 'Call', 'Meeting', 'Courier', 'InPerson', 'Message', 'Note', 'Other',
].map(v => ({ value: v, label: v }));

const BLANK = { log_type: 'Note', subject: '', body: '', attachment: null };

/**
 * Standalone "Log Communication" form, extracted from
 * CommunicationLogManagement.js's "New Log" form (the debt-recovery
 * status-change/settlement-task branch of that form is deliberately not
 * ported here - this is for logging a plain communication against one
 * case, not a debt-recovery workflow transition).
 */
export default function LogCommunicationModal({
  open,
  onClose,
  onCreated,
  authAxios,
  showFormMessage,
  caseId,
  clientId,
  communicationLogTypeOptions = DEFAULT_TYPE_OPTIONS,
}) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const fld = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const fileFld = (e) => setForm(f => ({ ...f, attachment: e.target.files?.[0] || null }));

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const basePayload = {
        case: caseId || null,
        client: clientId || null,
        log_type: form.log_type,
        subject: form.subject,
        body: form.body,
      };
      if (form.attachment) {
        const formData = new FormData();
        Object.entries(basePayload).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return;
          formData.append(key, value);
        });
        formData.append('attachment', form.attachment);
        await authAxios.post('communication-logs/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await authAxios.post('communication-logs/', basePayload);
      }
      showFormMessage?.('success', 'Communication logged successfully!');
      setForm(BLANK);
      onCreated?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data ? JSON.stringify(err.response.data) : (err?.message || 'Failed to log communication.');
      showFormMessage?.('danger', `Failed to log communication. ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [authAxios, form, caseId, clientId, showFormMessage, onCreated, onClose]);

  return (
    <Modal open={open} onClose={onClose} title="Log Communication" maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Type</label>
          <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
            name="log_type" value={form.log_type} onChange={fld} required>
            {communicationLogTypeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Subject</label>
          <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
            name="subject" value={form.subject} onChange={fld} autoFocus />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Summary</label>
          <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm" rows={4}
            name="body" value={form.body} onChange={fld} required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Attachment (optional)</label>
          <input type="file" className="mt-1 block w-full text-sm" onChange={fileFld} />
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
            {saving ? 'Logging…' : 'Log Communication'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
